import mixpanel, { Dict } from 'mixpanel-browser'
import { App } from '@capacitor/app'
import { Device } from '@capacitor/device'
import { warn, error } from '@/utils/logger'
import {
  type ContentInteractionProperties,
  type SearchProperties,
  type ContentCreationProperties,
  type ErrorProperties,
  EVENT_HIGH_INTENT_TRIP,
  EVENT_USER_PROFILE_UPDATE,
} from './analyticsEvents'
import {
  getReferringCreatorId,
  type HighIntentTripAnalyticsProps,
} from './analyticsAttribution'
import {
  type UtmParameters,
  type AcquisitionSource,
  type SourceType,
  getAcquisitionSource,
  getSourceType,
  hasCreatorShareAttribution,
} from './utm'
import { getPlatform, getSignupPlatform, isNativePlatform } from './platform'
import { isAnalyticsBotEnvironment } from './analyticsBotDetection'

/** Canonical profile_type values sent to Mixpanel (lowercase). */
export type AnalyticsProfileType = 'regular' | 'creator' | 'advisor' | 'anonymous'

const ANONYMOUS_PROFILE_TYPE: AnalyticsProfileType = 'anonymous'

export function normalizeProfileTypeForAnalytics(value: unknown): AnalyticsProfileType {
  const lower = typeof value === 'string' ? value.trim().toLowerCase() : ''
  if (lower === 'creator' || lower === 'advisor') return lower
  return 'regular'
}

/** Breakpoint (px) for desktop vs mobile; matches common Tailwind lg. */
const DESKTOP_BREAKPOINT = 1024
/** Breakpoint (px) for tablet (Tailwind md). */
const TABLET_BREAKPOINT = 768

export class WebAnalytics {
  private static initialized = false
  private static analyticsDisabled = false
  private static nativeDeviceInfoLoaded = false

  private static isActive(): boolean {
    return this.initialized && !this.analyticsDisabled
  }

  /** Vitest-only: reset singleton state between cases. */
  static resetForTesting(): void {
    if (process.env.NODE_ENV !== 'test') return
    this.initialized = false
    this.analyticsDisabled = false
    this.nativeDeviceInfoLoaded = false
  }

  /**
   * Returns device_type and viewport dimensions for all events.
   * Call only when initialized and on client (track() is no-op when not initialized).
   */
  private static getDeviceAndViewport(): {
    device_type: 'desktop' | 'tablet' | 'mobile'
    viewport_width?: number
    viewport_height?: number
  } {
    if (typeof window === 'undefined') {
      return { device_type: 'desktop' }
    }
    const width = window.innerWidth
    const device_type = width >= DESKTOP_BREAKPOINT
      ? 'desktop'
      : width >= TABLET_BREAKPOINT
        ? 'tablet'
        : 'mobile'
    return {
      device_type,
      viewport_width: width,
      viewport_height: window.innerHeight,
    }
  }

  static init() {
    if (this.initialized || this.analyticsDisabled) return

    if (isAnalyticsBotEnvironment()) {
      this.analyticsDisabled = true
      return
    }

    const token = process.env.NEXT_PUBLIC_MIXPANEL_TOKEN
    if (!token) {
      warn('Mixpanel token not found. Analytics tracking disabled.')
      return
    }

    mixpanel.init(token, {
      debug: process.env.NODE_ENV === 'development',
      track_pageview: false, // Manual tracking for better control
      persistence: 'localStorage',
      ignore_dnt: false
    })

    // Register platform super properties — merged into every event automatically.
    const platform = getPlatform()
    const native = isNativePlatform()
    mixpanel.register({
      platform,
      runtime: native ? 'capacitor' : 'browser',
      is_native_app: native,
    })

    this.initialized = true
    this.setAnonymousProfile()

    // Async: enrich with native device/app metadata (no-op on web).
    void this.loadNativeDeviceInfo()
  }

  /** Logged-out Mixpanel People profile (after init or logout reset). */
  static setAnonymousProfile(): void {
    if (!this.isActive()) return

    try {
      mixpanel.people.set({
        profile_type: ANONYMOUS_PROFILE_TYPE,
        is_logged_in: false,
        is_internal: false,
      })
    } catch (err) {
      error('Analytics setAnonymousProfile failed:', err)
    }
  }

  /**
   * Fetches native app and device metadata via Capacitor and registers them
   * as Mixpanel super properties. Called once after init on iOS/Android.
   * Silent no-op on web.
   */
  private static async loadNativeDeviceInfo(): Promise<void> {
    if (!isNativePlatform() || this.nativeDeviceInfoLoaded) return
    this.nativeDeviceInfoLoaded = true

    try {
      const [appInfo, deviceInfo] = await Promise.all([
        App.getInfo(),
        Device.getInfo(),
      ])

      mixpanel.register({
        app_version: appInfo.version,
        app_build: appInfo.build,
        os_version: deviceInfo.osVersion,
        device_model: deviceInfo.model,
      })
    } catch (err) {
      error('Failed to load native device info for analytics:', err)
    }
  }

  static trackVisitContent(
    pageName: string,
    metadata?: Record<string, unknown>
  ) {
    if (!this.isActive()) return

    try {
      const properties: Dict = {
        page_name: pageName,
        url: typeof window !== 'undefined' ? window.location.href : '',
        referrer: typeof document !== 'undefined' ? document.referrer : '',
        timestamp: new Date().toISOString(),
        ...this.getDeviceAndViewport(),
        ...metadata,
        ...this.getAcquisitionProps(),
      }

      mixpanel.track('Visit_Content', properties)
    } catch (err) {
      error('Analytics tracking failed:', err)
    }
  }

  static track(eventName: string, properties?: Record<string, unknown>) {
    if (!this.isActive()) return

    try {
      mixpanel.track(eventName, {
        timestamp: new Date().toISOString(),
        ...this.getDeviceAndViewport(),
        ...properties
      })
    } catch (err) {
      // Silently fail - analytics should never break user experience
      error('Analytics tracking failed:', err)
    }
  }

  static identify(userId: string) {
    if (!this.isActive()) return

    try {
      mixpanel.identify(userId)
    } catch (err) {
      error('Analytics identify failed:', err)
    }
  }

  static setUserProfile(user: {
    id: string
    email?: string
    name?: string
    profile_type?: string
    is_internal?: boolean
    [key: string]: unknown
  }) {
    if (!this.isActive()) return

    try {
      const profilePayload = { ...user, is_logged_in: true }
      if (user.profile_type === ANONYMOUS_PROFILE_TYPE) {
        profilePayload.profile_type = ANONYMOUS_PROFILE_TYPE
        profilePayload.is_logged_in = false
      } else if (user.profile_type !== undefined) {
        profilePayload.profile_type = normalizeProfileTypeForAnalytics(user.profile_type)
      } else {
        profilePayload.profile_type = 'regular'
      }
      profilePayload.is_internal = user.is_internal === true
      mixpanel.people.set({
        $email: user.email,
        $name: user.name,
        ...profilePayload
      })
    } catch (err) {
      error('Analytics setUserProfile failed:', err)
    }
  }

  static trackSignup(user: {
    id: string
    email?: string
    name?: string
    profile_type?: string
    [key: string]: unknown
  }, metadata?: Record<string, unknown>) {
    if (!this.isActive()) return

    try {
      const acquisitionProps = this.getAcquisitionProps()

      this.track('Signup', {
        user_id: user.id,
        email: user.email,
        name: user.name,
        profile_type: normalizeProfileTypeForAnalytics(user.profile_type),
        signup_platform: getSignupPlatform(),
        ...acquisitionProps,
        ...(metadata ?? {})
      })

      // Identify the user and set profile - this merges anonymous activity with identified user
      this.identify(user.id)
      this.setUserProfile({
        ...user,
        profile_type: normalizeProfileTypeForAnalytics(user.profile_type),
      })
    } catch (err) {
      error('Analytics trackSignup failed:', err)
    }
  }

  static reset() {
    if (!this.isActive()) return

    try {
      mixpanel.reset()
      this.setAnonymousProfile()
    } catch (err) {
      error('Analytics reset failed:', err)
    }
  }

  /**
   * Returns post-onboarding timing properties for content creation events.
   * Enables Mixpanel analysis of "did the user create content right after onboarding?"
   */
  static getNewUserProps(): {
    is_new_user: boolean
    minutes_since_onboarding: number | null
  } {
    if (typeof window === 'undefined') {
      return { is_new_user: false, minutes_since_onboarding: null }
    }

    try {
      const completedAt = localStorage.getItem('onboarding_completed_at')
      if (!completedAt) {
        return { is_new_user: false, minutes_since_onboarding: null }
      }

      const minutesSince = Math.round(
        (Date.now() - new Date(completedAt).getTime()) / 60000
      )
      return {
        is_new_user: minutesSince < 1440,
        minutes_since_onboarding: minutesSince,
      }
    } catch {
      return { is_new_user: false, minutes_since_onboarding: null }
    }
  }

  /**
   * Capture and persist UTM parameters for acquisition source calculation.
   * Note: Mixpanel JS SDK auto-tracks UTM params, but we store them in localStorage
   * to derive our custom acquisition_source property for signups that happen later.
   */
  static captureAcquisitionSource() {
    if (typeof window === 'undefined') return null

    const params = new URLSearchParams(window.location.search)
    const creatorIdParam = params.get('creator_id')
    const source = {
      utm_source: params.get('utm_source') || undefined,
      utm_medium: params.get('utm_medium') || undefined,
      utm_campaign: params.get('utm_campaign') || undefined,
      utm_content: params.get('utm_content') || undefined,
      utm_term: params.get('utm_term') || undefined,
      sr_type: params.get('sr_type') || undefined,
      sr_id: params.get('sr_id') || undefined,
      landing_page: window.location.pathname,
      ...(creatorIdParam ? { creator_id: creatorIdParam } : {}),
    }

    // Filter out undefined values
    const filteredSource = Object.fromEntries(
      Object.entries(source).filter(([_unused, v]) => v !== undefined)
    )

    // Persist UTM params for later acquisition_source calculation
    if (Object.keys(filteredSource).length > 0) {
      try {
        const existingRaw = localStorage.getItem('acquisition_source')
        const payload = {
          ...filteredSource,
          first_seen: new Date().toISOString(),
        }

        if (!existingRaw) {
          localStorage.setItem('acquisition_source', JSON.stringify(payload))
        } else {
          const existing = JSON.parse(existingRaw) as Record<string, unknown>
          const incomingHasCreator = hasCreatorShareAttribution(filteredSource)
          const storedHasCreator = hasCreatorShareAttribution(existing)

          if (incomingHasCreator && !storedHasCreator) {
            localStorage.setItem(
              'acquisition_source',
              JSON.stringify({
                ...filteredSource,
                first_seen:
                  typeof existing.first_seen === 'string'
                    ? existing.first_seen
                    : payload.first_seen,
              }),
            )
          }
        }
        // Note: Mixpanel JS SDK auto-registers UTM params as super properties
      } catch (e) {
        error('Failed to persist acquisition source:', e)
      }
    }

    return filteredSource
  }

  /**
   * Get stored acquisition source
   */
  static getAcquisitionSource(): Record<string, unknown> | null {
    if (typeof window === 'undefined') return null

    try {
      const stored = localStorage.getItem('acquisition_source')
      return stored ? JSON.parse(stored) : null
    } catch (_e) {
      return null
    }
  }

  private static storedSourceToUtmParams(
    storedSource: Record<string, unknown> | null
  ): UtmParameters | null {
    if (!storedSource) return null

    return {
      utm_source: storedSource.utm_source as string | undefined,
      utm_medium: storedSource.utm_medium as string | undefined,
      utm_campaign: storedSource.utm_campaign as string | undefined,
      utm_content: storedSource.utm_content as string | undefined,
      utm_term: storedSource.utm_term as string | undefined,
      creator_id: storedSource.creator_id as string | undefined,
      sr_type: storedSource.sr_type as string | undefined,
      sr_id: storedSource.sr_id as string | undefined,
    }
  }

  /**
   * Derived acquisition properties for Visit_Content, Signup, and logged-out funnels.
   */
  static getAcquisitionProps(): {
    acquisition_source: AcquisitionSource
    source_type: SourceType
    referring_creator_id?: string
    utm_content?: string
  } {
    const storedSource = this.getAcquisitionSource()
    const utmParams = this.storedSourceToUtmParams(storedSource)
    const searchParams =
      typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null
    const referringCreatorId = getReferringCreatorId(storedSource, searchParams)

    return {
      acquisition_source: getAcquisitionSource(utmParams),
      source_type: getSourceType(utmParams),
      ...(referringCreatorId ? { referring_creator_id: referringCreatorId } : {}),
      ...(typeof storedSource?.utm_content === 'string'
        ? { utm_content: storedSource.utm_content }
        : {}),
    }
  }

  /** Creator referral id from first-touch storage or current URL (for booking events). */
  static getReferringCreatorProps(): { referring_creator_id?: string } {
    if (typeof window === 'undefined') return {}

    const storedSource = this.getAcquisitionSource()
    const searchParams = new URLSearchParams(window.location.search)
    const referringCreatorId = getReferringCreatorId(storedSource, searchParams)
    return referringCreatorId ? { referring_creator_id: referringCreatorId } : {}
  }

  // ============================================================================
  // CONVENIENCE METHODS (matching mobile patterns)
  // ============================================================================

  /**
   * Track content interaction events (like, save, share, etc.)
   * Matches mobile's pattern for tracking post/content interactions
   */
  static trackContentInteraction(
    eventName: string,
    properties?: Partial<ContentInteractionProperties>
  ) {
    if (!this.isActive()) return

    this.track(eventName, properties)
  }

  /**
   * Track content creation events
   * Matches mobile's pattern for tracking post/list/itinerary creation
   */
  static trackContentCreation(
    eventName: string,
    properties?: Partial<ContentCreationProperties>
  ) {
    if (!this.isActive()) return

    this.track(eventName, properties)
  }

  /** High-intent booking CTA (Book / Check Availability) before whitelabel or in-app modal. */
  static trackHighIntentTrip(properties: HighIntentTripAnalyticsProps) {
    if (!this.isActive()) return

    this.track(EVENT_HIGH_INTENT_TRIP, {
      ...properties,
      ...this.getReferringCreatorProps(),
    })
  }

  /**
   * Track search-related events
   * Matches mobile's pattern for tracking search queries and autocomplete
   */
  static trackSearch(
    eventName: string,
    properties?: Partial<SearchProperties>
  ) {
    if (!this.isActive()) return

    this.track(eventName, properties)
  }

  /**
   * Track error events
   * Matches mobile's pattern for error tracking
   */
  static trackError(
    eventName: string,
    properties?: Partial<ErrorProperties>
  ) {
    if (!this.isActive()) return

    this.track(eventName, properties)
  }

  /**
   * Track button clicks
   * Matches mobile's trackButtonClick pattern
   */
  static trackButtonClick(
    buttonName: string,
    pageName?: string,
    additionalData?: Record<string, unknown>
  ) {
    if (!this.isActive()) return

    this.track(buttonName, {
      button_name: buttonName,
      page_name: pageName,
      ...additionalData
    })
  }

  /**
   * Track profile update
   * Matches mobile's User Profile Update event
   */
  static trackProfileUpdate(
    userId: string,
    updatedFields: Record<string, unknown>
  ) {
    if (!this.isActive()) return

    this.track(EVENT_USER_PROFILE_UPDATE, {
      user_id: userId,
      ...updatedFields
    })
  }

  // ============================================================================
  // LOGGED OUT USER TRACKING METHODS
  // ============================================================================

  /**
   * Track logged-out page view with common properties.
   * Note: UTM params and referrer are auto-tracked by Mixpanel JS SDK.
   */
  static trackLoggedOutPageView(
    eventName: string,
    pageName: string,
    properties?: Record<string, unknown>
  ) {
    if (!this.isActive()) return

    const acquisitionProps = this.getAcquisitionProps()

    this.track(eventName, {
      user_state: 'logged_out',
      page_name: pageName,
      ...acquisitionProps,
      ...properties
    })
  }

  /**
   * Track logged-out interaction event
   */
  static trackLoggedOutInteraction(
    eventName: string,
    pageName: string,
    properties?: Record<string, unknown>
  ) {
    if (!this.isActive()) return

    const acquisitionProps = this.getAcquisitionProps()

    this.track(eventName, {
      user_state: 'logged_out',
      page_name: pageName,
      ...acquisitionProps,
      ...properties
    })
  }

  /**
   * Track scroll depth with throttling
   */
  static trackScrollDepth(
    eventName: string,
    pageName: string,
    scrollDepthPercent: number,
    additionalProperties?: Record<string, unknown>
  ) {
    if (!this.isActive()) return

    this.track(eventName, {
      user_state: 'logged_out',
      page_name: pageName,
      scroll_depth_percent: scrollDepthPercent,
      ...additionalProperties
    })
  }
}
